import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import type { Vec2 } from '../../../geo/types';
import type { Frame, MeshBuilder } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { horizontalCap, octahedronSolid, planPoint, prismSolid, pyramidOverRing, ringPoints } from '../solids';
import { defensiveMasonryFace } from '../fortifications/towerMasonry';
import { buildVessel } from './churchVessel';
import type { VesselPlan } from './churchModel';
import { churchPlanOf } from './church';
import { parseChurchParams } from './churchParams';
import { centredWindow, rectWall, type MasonryStyles } from './masonry';
import { dominantAxis, meshFrameOf, planBounds, planFrameOf } from './planFrame';
import { LANDMARK_PATTERN, patterned } from './surfacePatterns';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';

export interface PaintedChapelColors {
  readonly wall: LinearRGB;
  readonly roof: LinearRGB;
  readonly dark: LinearRGB;
  readonly glass: LinearRGB;
  readonly dressing: LinearRGB;
}
interface Ids { readonly edgeId: number; readonly seed: number; }

/**
 * The share of the tower exaggeration the painter view gives the chapels, convents and halls: the
 * Nieuwe Kerk's, about twice their height. Read on the 6692 px scan against the model's own heights,
 * which are estimates: the Heilige Stede's gables stand 1.7 to 2.45 times as high as the painter
 * camera sees them, the Sint Olofskapel's 1.8 to 2.3 and the Nieuwe Nonnen's range 2.0 to 2.2
 * (docs/plans/landmarks.md 6.2). At the towers' whole 3.54 times they stood over both churches.
 */
export const PAINTED_CHAPEL_STYLE = { share: 0.4 } as const;

export function paintedChapelStyles(colors: PaintedChapelColors, ids: Ids,
  tracery: TracerySink): MasonryStyles {
  const base = { ...ids, strokeAngle: 0, exaggeration: { base: 0, share: PAINTED_CHAPEL_STYLE.share } };
  const wall = patterned({ ...base, color: colors.wall, surface: SURFACE.stone,
    extent: [20, 12] }, LANDMARK_PATTERN.lateGothic);
  const opening = { ...base, color: colors.dark, surface: SURFACE.linework, extent: [1, 1] as const };
  return { wall,
    roof: patterned({ ...base, color: colors.roof, surface: SURFACE.roofSlate,
      extent: [20, 6] }, LANDMARK_PATTERN.slate),
    opening,
    glazing: { reveal: { ...wall, color: colors.dressing },
      leaded: patterned({ ...opening, color: colors.glass }, LANDMARK_PATTERN.glass),
      tracery: { sink: tracery, edgeId: ids.edgeId } } };
}

/** Three joined vessels and the substantial eastern roof belfry are plainly visible in
 * the 1538 painting and 1544 woodcut. Dimensions and hidden construction are estimates
 * fitted inside the old 40×14 m reservation, not the later enlarged chapel's surveyed plan. */
export function heiligeStedePlan(landmark: Landmark) {
  const plan = planFrameOf(landmark.footprint, dominantAxis(landmark.footprint, landmark.heading));
  const b = planBounds(plan), width = b.v1 - b.v0, length = b.u1 - b.u0;
  const vc = (b.v0 + b.v1) / 2;
  const centralHalf = width * 0.17;
  const sideHalf = width * 0.12;
  const main: VesselPlan = { u0: b.u0 + 1.2, chordU: b.u1 - centralHalf - 1.2,
    vc, half: centralHalf, eaveY: 12, ridgeY: 16.6, apse: 'polygonal' };
  const side = (sign: number): VesselPlan => ({ u0: main.u0 + 0.7,
    chordU: main.chordU - 1.5, vc: vc + sign * (centralHalf + sideHalf),
    half: sideHalf, eaveY: 11.2, ridgeY: sign > 0 ? 14.7 : 14.3, apse: 'polygonal' });
  return { frame: meshFrameOf(plan), main, sides: [side(-1), side(1)],
    belfry: { centre: [b.u0 + length * 0.71, vc] as Vec2,
      radius: 1.05, footY: 16.0, eaveY: 20.0, topY: 24.4 } };
}

export function buildHeiligeStede(builder: MeshBuilder, landmark: Landmark,
  colors: PaintedChapelColors, ids: Ids, tracery: TracerySink = NO_TRACERY): number {
  const before = builder.triangleCount, styles = paintedChapelStyles(colors, ids, tracery);
  const { frame, main, sides, belfry } = heiligeStedePlan(landmark);
  for (const vessel of [main, ...sides]) buildVessel(builder, styles, frame, vessel,
    { longWindows: { sill: 2.6, top: vessel.eaveY - 1.3 }, westWindow: true, bay: 5.2 });
  const ring = ringPoints(belfry.centre, belfry.radius, 8, Math.PI / 8);
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    const width = Math.hypot(b[0] - a[0], b[1] - a[1]);
    rectWall(builder, styles, frame, [a, b], [belfry.footY, belfry.eaveY],
      centredWindow(width, 1.2, 3.2, 0.5), belfry.footY);
  });
  horizontalCap(builder, styles.wall, frame, ring, belfry.footY, false);
  pyramidOverRing(builder, styles, frame, ring, belfry.eaveY,
    { plan: belfry.centre, y: belfry.topY });
  octahedronSolid(builder, styles.wall, frame,
    planPoint(belfry.centre[0], belfry.topY + 0.12, belfry.centre[1]), 0.2);
  return builder.triangleCount - before;
}

export const BEGIJNHOF_CHAPEL = { naveHeight: 11, naveWidth: 8.8,
  towerHeight: 15.7, towerWidth: 3.4, capRise: 3.7 } as const;

/** The west square belfry was absent because the old source set a spire but no tower
 * height. Use the standing church footprint only to fit the period-image component types;
 * the 1671 Catholic schuilkerk opposite it is not introduced. */
export function begijnhofChapelPlan(landmark: Landmark) {
  const p = parseChurchParams(landmark, () => {});
  const c = BEGIJNHOF_CHAPEL;
  return churchPlanOf(landmark, { ...p, naveHeight: c.naveHeight, naveWidth: c.naveWidth,
    aisles: 0, towerHeight: c.towerHeight, towerWidth: c.towerWidth,
    spire: 'pyramid', spireHeight: c.capRise, ridgeTurret: false, apse: 'polygonal' });
}

function begijnhofTower(builder: MeshBuilder, styles: MasonryStyles, frame: Frame,
  tower: NonNullable<ReturnType<typeof begijnhofChapelPlan>['plan']['tower']>): void {
  const uc = (tower.u0 + tower.u1) / 2, vc = tower.vc, half = tower.half;
  const ring: Vec2[] = [[uc - half, vc - half], [uc + half, vc - half],
    [uc + half, vc + half], [uc - half, vc + half]];
  ring.forEach((a, i) => {
    defensiveMasonryFace(builder, styles.wall, frame,
      a, ring[(i + 1) % ring.length]!, 0, tower.height,
      [0.28, 0.72].map((f) => ({ x: tower.half * 2 * f - 0.22,
        y: tower.height - 4.4, width: 0.44, height: 2.7, cuts: [] })), styles.opening);
  });
  // Two physical horizontal bands articulate the little tower's stages.
  for (const y of [8.7, 11.0]) {
    const belt = ring.map(([u, v]): Vec2 => [uc + (u - uc) * 1.025, vc + (v - vc) * 1.025]);
    prismSolid(builder, styles.wall, frame, belt, y, y + 0.18, { top: true, bottom: true });
  }
  pyramidOverRing(builder, styles, frame, ring, tower.height,
    { plan: [uc, vc], y: tower.height + tower.spireHeight });
  octahedronSolid(builder, styles.wall, frame,
    planPoint(uc, tower.height + tower.spireHeight + 0.12, vc), 0.16);
}

export function buildBegijnhofkapel(builder: MeshBuilder, landmark: Landmark,
  colors: PaintedChapelColors, ids: Ids, tracery: TracerySink = NO_TRACERY): number {
  const before = builder.triangleCount, styles = paintedChapelStyles(colors, ids, tracery);
  const planned = begijnhofChapelPlan(landmark), frame = meshFrameOf(planned.frame);
  const { vessel, tower, westBay } = planned.plan;
  if (tower) begijnhofTower(builder, styles, frame, tower);
  if (westBay) buildVessel(builder, styles, frame, westBay,
    { longWindows: null, westWindow: false, bay: 5.2 });
  buildVessel(builder, styles, frame, vessel,
    { longWindows: { sill: 2.6, top: vessel.eaveY - 1.0 }, westWindow: !tower, bay: 5.2 });
  return builder.triangleCount - before;
}
